import * as React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { useAuth } from '../auth/AuthContext';
import { getPrimaryDisplayRole } from '../auth/authUtils';
import { APP_ROLES, type AppRole } from '../auth/role-based-access-control';

export default function UserMenu() {
  const {
    user,
    realmRoles,
    logout,
    isDevRoleOverrideEnabled,
    devRoleOverride,
    setDevRoleOverride,
  } = useAuth();
  const displayRole = getPrimaryDisplayRole(realmRoles);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuId = React.useId()?.replace(/:/g, '') || 'user-menu';

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const displayName = user?.name ?? user?.email ?? 'User';

  return (
    <>
      <IconButton
        aria-label={user ? 'Account menu' : 'Login'}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleOpen}
        size="small"
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <PersonRoundedIcon fontSize="small" />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260, maxWidth: 320 } } }}
      >
        {user ? [
          (
            <Box key="header" sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {displayName}
              </Typography>
              {user.email && user.name !== user.email && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    lineHeight: 1.45,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {user.email}
                </Typography>
              )}
              {displayRole && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={displayRole}
                    variant="outlined"
                    color="primary"
                    sx={{
                      height: 22,
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          ),
          ...(isDevRoleOverrideEnabled
            ? [
                <Divider key="dev-divider" />,
                (
                  <Box key="dev-role" sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      DEV ROLE OVERRIDE
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                      <InputLabel id={`${menuId}-dev-role-label`}>Role</InputLabel>
                      <Select
                        labelId={`${menuId}-dev-role-label`}
                        label="Role"
                        value={devRoleOverride ?? ''}
                        onChange={(event) => {
                          const next = (event.target.value || null) as AppRole | null;
                          setDevRoleOverride(next);
                        }}
                      >
                        <MenuItem value="">
                          <em>Token role</em>
                        </MenuItem>
                        {Object.values(APP_ROLES).map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ),
              ]
            : []),
          <Divider key="divider" />,
          (
            <MenuItem key="logout" onClick={handleLogout}>
              <ListItemIcon>
                <LogoutRoundedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Log out</ListItemText>
            </MenuItem>
          ),
        ] : (
          <MenuItem
            component="a"
            href="/"
            onClick={handleClose}
          >
            <ListItemIcon>
              <PersonRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign in</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
